export interface Subscription {
  id: string;
  label: string;
  entityType: 'device' | 'gateway';
  topics: string[];
  messageCount: number;
}

export interface MqttMessage {
  id: number;
  topic: string;
  message: Record<string, unknown>;
  timestamp: number;
  user?: string;
  appid?: string;
  deveui?: string;
  gwid?: string;
  type?: string;
  port?: string;
  shared?: string;
}

export interface SubscribeParams {
  entityType: 'device' | 'gateway';
  entityId: string;
  label: string;
  appId?: number;
  owner?: string;
  isShared?: boolean;
}

const REGEX_UPLINK =
  /(?:\/|)sub\/(?:v1|v2)\/users\/(?<user>[^/]+)(?:\/shared\/(?<shared>[^/]+))?\/apps\/(?<appid>\d+)\/devices\/(?<deveui>[a-fA-F0-9]{1,16})\/(?<type>uplink|downlink)\/(?<port>\d+)/;
const REGEX_EVENT =
  /(?:\/|)sub\/(?:v1|v2)\/users\/(?<user>[^/]+)(?:\/shared\/(?<shared>[^/]+))?\/apps\/(?<appid>\d+)\/devices\/(?<deveui>[a-fA-F0-9]{1,16})\/events\/(?<type>\S+)/;
const REGEX_ALERT =
  /(?:\/|)sub\/(?:v1|v2)\/users\/(?<user>[^/]+)(?:\/shared\/(?<shared>[^/]+))?\/apps\/(?<appid>\d+)\/devices\/(?<deveui>[a-fA-F0-9]{1,16})\/alerts\/(?<type>[-a-zA-Z]+)/;
const REGEX_EVENT_GW =
  /(?:\/|)sub\/(?:v1|v2)\/users\/(?<user>[^/]+)(?:\/shared\/(?<shared>[^/]+))?\/gateways\/(?<gwid>[a-fA-F0-9]{1,16})\/events\/(?<type>\S+)/;
const REGEX_ALERT_GW =
  /(?:\/|)sub\/(?:v1|v2)\/users\/(?<user>[^/]+)(?:\/shared\/(?<shared>[^/]+))?\/gateways\/(?<gwid>[a-fA-F0-9]{1,16})\/alerts\/(?<type>[-a-zA-Z]+)/;

const TOPIC_MATCHERS = [REGEX_UPLINK, REGEX_EVENT, REGEX_ALERT, REGEX_EVENT_GW, REGEX_ALERT_GW];

const safeParseJson = (str: string): Record<string, unknown> => {
  try {
    return JSON.parse(str) as Record<string, unknown>;
  } catch {
    return { payload: str };
  }
};

export const parseMessage = (topic: string, rawMessage: string, id: number): MqttMessage => {
  const base: MqttMessage = {
    id,
    topic,
    timestamp: Date.now(),
    message: safeParseJson(rawMessage),
  };

  for (const regex of TOPIC_MATCHERS) {
    const match = topic.match(regex);
    if (match?.groups) {
      return { ...base, ...match.groups };
    }
  }

  return base;
};

export const buildDeviceTopics = (params: {
  prefix: string;
  username: string;
  appId: number;
  deveui: string;
  owner?: string;
  isShared?: boolean;
}): string[] => {
  const { prefix, username, appId, deveui, owner, isShared } = params;
  const base =
    isShared && owner
      ? `${prefix}/users/${owner}/shared/${username}/apps/${appId}/devices/${deveui}`
      : `${prefix}/users/${username}/apps/${appId}/devices/${deveui}`;
  return [`${base}/uplink/#`, `${base}/downlink/#`, `${base}/events/#`, `${base}/alerts/#`];
};

export const buildGatewayTopics = (params: {
  prefix: string;
  username: string;
  gwid: string;
}): string[] => {
  const { prefix, username, gwid } = params;
  const base = `${prefix}/users/${username}/gateways/${gwid}`;
  return [`${base}/events/#`, `${base}/alerts/#`];
};
